import * as React from 'react';
import AppTheme from '../../../theme/AppTheme';
import Box from '@mui/material/Box';
import { brand } from '../../../theme/themePrimitives';
import CssBaseline from '@mui/material/CssBaseline';
import CustomInput from '../../../components/reusable/input';
import ErrorModal from '../../../components/modals/error_modal';
import { forgetPasswordSchema } from '../../../schema/auth_schema';
import ImageClass from '../../../assets';
import { RouteChangeToastCleaner } from '../../../route_toast_cleaner';
import { sendEmailForResettingPasswordThunk } from '../../../redux/thunks';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router';
import { AppDispatch, RootState } from '../../../redux/store';
import { Avatar, Button, CircularProgress } from '@mui/material';
import { Form, Formik } from 'formik';
import { styled, useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: Singup Page
 * @paragraph: This is the Signup page component.
 * @heading4: Styled Components.
 * @ol:
 * Styled page
 * Page Container
 * Image Container
 * Form Container
 * Styled Form
 * @separator:
 */
const StyledPage = styled(Box)(({ theme }) => ({
    padding: '2rem 5rem',
    display: 'flex',
    height: '100vh',
    [theme.breakpoints.down('sm')]: {
        padding: '5rem 0'
    }
}));

const PageContainer = styled(Box)(({ theme }) => ({
    flex: '1',
    display: 'flex',
    // height: '100vh',
    borderRadius: '10px',
    padding: '10vh 10vw',
    // boxShadow: '1px 1px 1px 1px #f4f4f4',
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column'
    }
}));

const ImageContainer = styled(Box)(({ theme }) => ({
    flex: 2,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: brand[300],
    borderTopLeftRadius: '10px',
    borderBottomLeftRadius: '10px',
    [theme.breakpoints.down('sm')]: {
        height: '50%',
        display: 'none'
    }
}));

const FormContainer = styled(Box)(({ theme }) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    borderTop: '1px solid #ededed',
    borderRight: '1px solid #ededed',
    borderBottom: '1px solid #ededed',
    borderTopRightRadius: '10px',
    borderBottomRightRadius: '10px',
    padding: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
        height: '50%'
    }
}));
/**
 * @heading2: Signup Page Component
 * @heading3: Variables
 * @ol:
 * initialValues : {SignUpParams} - signup initial values.
 * dispatch : {AppDispatch} - The Redux dispatch function for triggering state updates.
 * navigate - Navigate for routing purpose.
 * isLoading - getting isLoading from a redux state/app.
 * token - getting token from a redux state/app.
 * @separator:
 */
const ForgetPasswordPage = (props: { disableCustomTheme?: boolean }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const dispatch = useDispatch<AppDispatch>();
    const { token, isLoading } = useSelector((state: RootState) => state.app);

    /**
     * @heading3: Functions
     * @heading4: useEffect
     * @paragraph: Redirects the user to the dashboard if a valid token exists.
     */
    React.useEffect(() => {
        if (token) {
            navigate('/app/dashboard');
        }
    }, [token, navigate]);

    /**
     * @heading4: handleSubmit
     * @paragraph: Performs Signup functionality
     * @bold: Paramter
     * @ol:
     * values : SignUpParams - values are first_name, last_name, organisation_name, email and password
     */
    const onSubmitHandler = async (values: {
        email: string,
    }) => {
        try {
            await dispatch(sendEmailForResettingPasswordThunk(values.email)).unwrap();
            // Example Navigation with Toast State
            navigate('/login', { state: { toastMessage: 'Password reset link sent!' }});
        } catch (e) {
            //
        }
    };

    return <AppTheme {...props}>
        <CssBaseline enableColorScheme />
        <StyledPage>
            <PageContainer>
                {/* Image Section */}
                <ImageContainer>
                    <Typography color="white" align="center" variant="h3" whiteSpace="wrap" fontWeight="bold" gutterBottom>Welcome to</Typography>
                    <img
                        src={ImageClass.customerSignup}
                        alt="customer_signup"
                        style={{
                            height: '40vh'
                        }}
                    />
                </ImageContainer>

                {/* Login Form Section */}
                <FormContainer>
                    {isLoading
                        ? <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                            <CircularProgress color="primary" />
                            <Typography>Sending Email...</Typography>
                        </Box>
                        : <Box>
                            <Avatar
                                sx={{ width: 'auto', height: 60, bgcolor: 'white' }}
                                src={theme.palette.mode === 'dark' ? ImageClass.logoDark : ImageClass.logoLight}
                                variant="square"
                            />
                            <Typography variant="body1" fontSize="24px" fontWeight="600" align="center" marginBottom={1}>
                                Reset Your Password
                            </Typography>
                            <Typography variant="body1" align="center" marginBottom={4}>
                                Enter the email associated with your account and we&apos;ll send you password reset instructions.
                            </Typography>
                            <Formik initialValues={{
                                email: ''
                            }} validationSchema={forgetPasswordSchema} onSubmit={onSubmitHandler}>
                                {({ values, handleChange, handleBlur, errors, touched, isValid }) => (
                                    <Form>
                                        <Box display="flex" flexDirection="column" gap={2}>
                                            <CustomInput
                                                type="email"
                                                label="Email"
                                                placeholder="Type here"
                                                value={values.email}
                                                onChange={handleChange('email')}
                                                onBlur={handleBlur('email')}
                                                error={touched.email && errors.email}
                                                disabled={isLoading}
                                                fullWidth
                                            />
                                            <Button variant="contained" fullWidth type="submit" disabled={!isValid || isLoading} >Submit</Button>
                                        </Box>
                                    </Form>
                                )}
                            </Formik>
                        </Box>}
                </FormContainer>
            </PageContainer>
        </StyledPage>
        <ErrorModal />
            <RouteChangeToastCleaner />

    </AppTheme>;
};
export default ForgetPasswordPage;